import FolderOutlined from "@mui/icons-material/FolderOutlined";
import type { CardProps } from "@mui/material";
import { Box, Card, CardActionArea, CardContent, Chip, Stack, Typography } from "@mui/material";
import type { DragEvent, FC } from "react";
import { useDragState } from "./hooks/use-drag-state";
import type { Folder } from "./server/folder-service";

export const FolderCard: FC<
  {
    folder: Folder;
    onClick: () => void;
  } & CardProps
> = ({ folder, onClick, onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop, ...props }) => {
  const { isDragging, showDropHighlight, handlers } = useDragState<HTMLDivElement>({
    onDragStart: onDragStart as (event: DragEvent<HTMLDivElement>) => void,
    onDragEnd: onDragEnd as (event: DragEvent<HTMLDivElement>) => void,
    onDragOver: onDragOver as (event: DragEvent<HTMLDivElement>) => void,
    onDragLeave: onDragLeave as (event: DragEvent<HTMLDivElement>) => void,
    onDrop: onDrop as (event: DragEvent<HTMLDivElement>) => void,
  });

  return (
    <Card
      variant="outlined"
      {...props}
      onDragStart={handlers.handleDragStart}
      onDragEnd={handlers.handleDragEnd}
      onDragOver={handlers.handleDragOver}
      onDragLeave={handlers.handleDragLeave}
      onDrop={handlers.handleDrop}
      onClick={onClick}
      sx={{
        cursor: props.draggable ? (isDragging ? "grabbing" : "grab") : undefined,
        opacity: isDragging ? 0.5 : 1,
        outline: showDropHighlight ? "2px solid" : "none",
        outlineColor: "primary.main",
        outlineOffset: -2,
        ...props.sx,
      }}
    >
      <CardActionArea
        sx={{
          cursor: props.draggable ? "inherit" : undefined,
        }}
      >
        <Box
          role="img"
          sx={{
            height: "200px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <FolderOutlined sx={{ width: "128px", height: "128px", color: "primary.main" }} />
        </Box>

        <CardContent>
          <Stack spacing={2}>
            <Typography variant="subtitle1" noWrap textOverflow="ellipsis">
              {folder.name}
            </Typography>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Chip label="Folder" />
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
